/**
 * SKOP Creator Network
 *
 * Analyzes and visualizes relationships between token creators,
 * exposing connected rug networks and suspicious patterns.
 */

// Node types in the network
export type NodeType = 'creator' | 'token' | 'funder' | 'cluster';

export interface NetworkNode {
  id: string;
  type: NodeType;
  label: string;
  // Position for visualization
  x?: number;
  y?: number;
  // Metadata
  data: CreatorNodeData | TokenNodeData | FunderNodeData | ClusterNodeData;
}

export interface CreatorNodeData {
  walletAddress: string;
  totalTokens: number;
  ruggedTokens: number;
  successfulTokens: number;
  totalVolumeUsd: number;
  reputation: number; // 0-100
  firstSeen: number;
  lastActive: number;
  aliases?: string[]; // Known wallet aliases
}

export interface TokenNodeData {
  mintAddress: string;
  symbol: string;
  name: string;
  createdAt: number;
  status: 'active' | 'rugged' | 'graduated' | 'dead';
  peakMarketCap: number;
  creatorProfit: number; // SOL
  holdersRugged: number;
}

export interface FunderNodeData {
  walletAddress: string;
  fundedCreators: number;
  totalFundingAmount: number;
  suspicionScore: number; // 0-100
}

export interface ClusterNodeData {
  clusterName: string;
  memberCount: number;
  totalRugs: number;
  identifiedPattern: string;
}

export interface NetworkEdge {
  id: string;
  source: string; // node id
  target: string; // node id
  type: EdgeType;
  weight: number; // 0-1, strength of connection
  metadata?: Record<string, unknown>;
}

export type EdgeType =
  | 'created' // creator → token
  | 'funded' // funder → creator
  | 'related' // creator → creator (shared patterns)
  | 'cluster_member' // cluster → creator/token
  | 'suspicious'; // flagged connection

export interface CreatorNetwork {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
  stats: NetworkStats;
}

export interface NetworkStats {
  totalCreators: number;
  totalTokens: number;
  totalRugs: number;
  identifiedClusters: number;
  suspiciousConnections: number;
  avgRugRate: number;
}

// Storage key
const NETWORK_CACHE_KEY = 'skop_creator_network';
const CACHE_TTL = 15 * 60 * 1000; // 15 minutes

/**
 * Build a creator network from a starting point
 */
export function buildCreatorNetwork(
  creatorAddress: string,
  depth: number = 2
): CreatorNetwork {
  // Check cache first
  const cached = getCachedNetwork(creatorAddress);
  if (cached) return cached;

  // In production, this would fetch from backend/blockchain
  // For now, generate mock data to demonstrate the visualization
  const network = generateMockNetwork(creatorAddress, depth);

  // Cache the result
  cacheNetwork(creatorAddress, network);

  return network;
}

/**
 * Get cached network if available
 */
function getCachedNetwork(creatorAddress: string): CreatorNetwork | null {
  try {
    const key = `${NETWORK_CACHE_KEY}_${creatorAddress}`;
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_TTL) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch {
    return null;
  }
}

/**
 * Cache network data
 */
function cacheNetwork(creatorAddress: string, network: CreatorNetwork): void {
  try {
    const key = `${NETWORK_CACHE_KEY}_${creatorAddress}`;
    localStorage.setItem(key, JSON.stringify({
      data: network,
      timestamp: Date.now(),
    }));
  } catch {
    // Storage full or unavailable
  }
}

/**
 * Generate mock network data for demonstration
 */
function generateMockNetwork(creatorAddress: string, depth: number): CreatorNetwork {
  const nodes: NetworkNode[] = [];
  const edges: NetworkEdge[] = [];

  // Add the main creator
  const mainCreator: NetworkNode = {
    id: creatorAddress,
    type: 'creator',
    label: shortenAddress(creatorAddress),
    data: {
      walletAddress: creatorAddress,
      totalTokens: Math.floor(Math.random() * 10) + 1,
      ruggedTokens: Math.floor(Math.random() * 5),
      successfulTokens: Math.floor(Math.random() * 3),
      totalVolumeUsd: Math.random() * 100000,
      reputation: Math.floor(Math.random() * 100),
      firstSeen: Date.now() - Math.random() * 90 * 24 * 60 * 60 * 1000,
      lastActive: Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000,
    } as CreatorNodeData,
  };
  nodes.push(mainCreator);

  // Generate tokens for main creator
  const creatorData = mainCreator.data as CreatorNodeData;
  for (let i = 0; i < creatorData.totalTokens; i++) {
    const tokenId = `token_${creatorAddress.slice(0, 8)}_${i}`;
    const isRugged = i < creatorData.ruggedTokens;
    const isGraduated = !isRugged && i < creatorData.ruggedTokens + creatorData.successfulTokens;

    const tokenNode: NetworkNode = {
      id: tokenId,
      type: 'token',
      label: generateTokenSymbol(),
      data: {
        mintAddress: tokenId,
        symbol: generateTokenSymbol(),
        name: `Token ${i + 1}`,
        createdAt: Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
        status: isRugged ? 'rugged' : isGraduated ? 'graduated' : 'active',
        peakMarketCap: Math.random() * 500000,
        creatorProfit: isRugged ? Math.random() * 50 : 0,
        holdersRugged: isRugged ? Math.floor(Math.random() * 500) : 0,
      } as TokenNodeData,
    };
    nodes.push(tokenNode);

    edges.push({
      id: `edge_${creatorAddress}_${tokenId}`,
      source: creatorAddress,
      target: tokenId,
      type: 'created',
      weight: 1,
    });
  }

  // Add related creators if depth > 1
  if (depth > 1) {
    const relatedCount = Math.floor(Math.random() * 3) + 1;
    for (let i = 0; i < relatedCount; i++) {
      const relatedAddress = generateAddress();
      const relatedCreator: NetworkNode = {
        id: relatedAddress,
        type: 'creator',
        label: shortenAddress(relatedAddress),
        data: {
          walletAddress: relatedAddress,
          totalTokens: Math.floor(Math.random() * 5) + 1,
          ruggedTokens: Math.floor(Math.random() * 3),
          successfulTokens: Math.floor(Math.random() * 2),
          totalVolumeUsd: Math.random() * 50000,
          reputation: Math.floor(Math.random() * 100),
          firstSeen: Date.now() - Math.random() * 60 * 24 * 60 * 60 * 1000,
          lastActive: Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000,
        } as CreatorNodeData,
      };
      nodes.push(relatedCreator);

      // Connection between creators
      edges.push({
        id: `edge_${creatorAddress}_${relatedAddress}`,
        source: creatorAddress,
        target: relatedAddress,
        type: 'related',
        weight: Math.random() * 0.5 + 0.5,
        metadata: {
          reason: ['Shared funding source', 'Similar patterns', 'Wallet cluster'][
            Math.floor(Math.random() * 3)
          ],
        },
      });

      // Add tokens for related creator
      const relatedData = relatedCreator.data as CreatorNodeData;
      for (let j = 0; j < relatedData.totalTokens; j++) {
        const tokenId = `token_${relatedAddress.slice(0, 8)}_${j}`;
        const isRugged = j < relatedData.ruggedTokens;

        const tokenNode: NetworkNode = {
          id: tokenId,
          type: 'token',
          label: generateTokenSymbol(),
          data: {
            mintAddress: tokenId,
            symbol: generateTokenSymbol(),
            name: `Token ${j + 1}`,
            createdAt: Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000,
            status: isRugged ? 'rugged' : 'active',
            peakMarketCap: Math.random() * 200000,
            creatorProfit: isRugged ? Math.random() * 30 : 0,
            holdersRugged: isRugged ? Math.floor(Math.random() * 300) : 0,
          } as TokenNodeData,
        };
        nodes.push(tokenNode);

        edges.push({
          id: `edge_${relatedAddress}_${tokenId}`,
          source: relatedAddress,
          target: tokenId,
          type: 'created',
          weight: 1,
        });
      }
    }
  }

  // Add a funder node if there are suspicious patterns
  if (creatorData.ruggedTokens > 2) {
    const funderAddress = generateAddress();
    const funderNode: NetworkNode = {
      id: funderAddress,
      type: 'funder',
      label: `Funder ${shortenAddress(funderAddress)}`,
      data: {
        walletAddress: funderAddress,
        fundedCreators: Math.floor(Math.random() * 5) + 2,
        totalFundingAmount: Math.random() * 100,
        suspicionScore: 70 + Math.floor(Math.random() * 30),
      } as FunderNodeData,
    };
    nodes.push(funderNode);

    edges.push({
      id: `edge_${funderAddress}_${creatorAddress}`,
      source: funderAddress,
      target: creatorAddress,
      type: 'funded',
      weight: 0.8,
    });
  }

  // Calculate positions using force-directed layout simulation
  calculateNodePositions(nodes, edges);

  // Calculate stats
  const stats = calculateNetworkStats(nodes, edges);

  return { nodes, edges, stats };
}

/**
 * Simple force-directed layout for positioning nodes
 */
function calculateNodePositions(nodes: NetworkNode[], edges: NetworkEdge[]): void {
  const width = 600;
  const height = 400;
  const centerX = width / 2;
  const centerY = height / 2;

  // Initialize random positions
  nodes.forEach((node, i) => {
    const angle = (i / nodes.length) * 2 * Math.PI;
    const radius = 100 + Math.random() * 100;
    node.x = centerX + Math.cos(angle) * radius;
    node.y = centerY + Math.sin(angle) * radius;
  });

  // Simple force simulation (10 iterations)
  for (let iter = 0; iter < 10; iter++) {
    // Repulsion between all nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[j].x! - nodes[i].x!;
        const dy = nodes[j].y! - nodes[i].y!;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = 1000 / (dist * dist);

        nodes[i].x! -= (dx / dist) * force;
        nodes[i].y! -= (dy / dist) * force;
        nodes[j].x! += (dx / dist) * force;
        nodes[j].y! += (dy / dist) * force;
      }
    }

    // Attraction along edges
    edges.forEach((edge) => {
      const source = nodes.find((n) => n.id === edge.source);
      const target = nodes.find((n) => n.id === edge.target);
      if (!source || !target) return;

      const dx = target.x! - source.x!;
      const dy = target.y! - source.y!;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const force = dist * 0.01 * edge.weight;

      source.x! += (dx / dist) * force;
      source.y! += (dy / dist) * force;
      target.x! -= (dx / dist) * force;
      target.y! -= (dy / dist) * force;
    });

    // Keep nodes in bounds
    nodes.forEach((node) => {
      node.x = Math.max(50, Math.min(width - 50, node.x!));
      node.y = Math.max(50, Math.min(height - 50, node.y!));
    });
  }
}

/**
 * Calculate network statistics
 */
function calculateNetworkStats(nodes: NetworkNode[], edges: NetworkEdge[]): NetworkStats {
  const creators = nodes.filter((n) => n.type === 'creator');
  const tokens = nodes.filter((n) => n.type === 'token');
  const ruggedTokens = tokens.filter(
    (n) => (n.data as TokenNodeData).status === 'rugged'
  );

  return {
    totalCreators: creators.length,
    totalTokens: tokens.length,
    totalRugs: ruggedTokens.length,
    identifiedClusters: nodes.filter((n) => n.type === 'cluster').length,
    suspiciousConnections: edges.filter((e) => e.type === 'suspicious').length,
    avgRugRate: tokens.length > 0 ? ruggedTokens.length / tokens.length : 0,
  };
}

// Helper functions
function shortenAddress(address: string): string {
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

function generateAddress(): string {
  const chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  let result = '';
  for (let i = 0; i < 44; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function generateTokenSymbol(): string {
  const prefixes = ['PEPE', 'DOGE', 'SHIB', 'BONK', 'WIF', 'CAT', 'MOON', 'PUMP', 'MEME'];
  const suffixes = ['', '2.0', 'INU', 'AI', 'GPT', 'X', 'PRO'];
  return prefixes[Math.floor(Math.random() * prefixes.length)] +
         suffixes[Math.floor(Math.random() * suffixes.length)];
}

/**
 * Get color for node type
 */
export function getNodeColor(type: NodeType): string {
  switch (type) {
    case 'creator':
      return '#00f0ff'; // cyan
    case 'token':
      return '#00ff88'; // green
    case 'funder':
      return '#ffb800'; // amber
    case 'cluster':
      return '#ff3b3b'; // red
    default:
      return '#888888';
  }
}

/**
 * Get color for edge type
 */
export function getEdgeColor(type: EdgeType): string {
  switch (type) {
    case 'created':
      return '#00ff8840';
    case 'funded':
      return '#ffb80060';
    case 'related':
      return '#00f0ff40';
    case 'suspicious':
      return '#ff3b3b80';
    default:
      return '#88888840';
  }
}

/**
 * Get status color for tokens
 */
export function getStatusColor(status: TokenNodeData['status']): string {
  switch (status) {
    case 'graduated':
      return '#00ff88';
    case 'active':
      return '#00f0ff';
    case 'rugged':
      return '#ff3b3b';
    case 'dead':
      return '#888888';
    default:
      return '#888888';
  }
}
